import React, { useState, useEffect, useCallback } from "react";
import { buildApiUrl, API_ENDPOINTS, APP_DISPLAY_NAME } from "../../config/api";
import { apiRequest } from "../../utils/apiUtils";

const s = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: "20px",
    boxSizing: "border-box",
  },
  card: {
    backgroundColor: "var(--bg-primary)",
    borderRadius: "16px",
    boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
    padding: "40px",
    width: "100%",
    maxWidth: "480px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxSizing: "border-box",
  },
  title: {
    fontSize: "18px",
    fontWeight: "700",
    color: "var(--text-primary)",
    margin: 0,
    textAlign: "center",
  },
  subtitle: {
    fontSize: "12px",
    color: "var(--text-secondary)",
    textAlign: "center",
    margin: 0,
    lineHeight: 1.6,
    width: "100%",
  },
  label: {
    fontSize: "11px",
    fontWeight: "500",
    color: "var(--text-secondary)",
  },
  input: {
    width: "100%",
    padding: "11px 14px",
    border: "1.5px solid var(--input-border)",
    borderRadius: "8px",
    fontSize: "20px",
    letterSpacing: "8px",
    textAlign: "center",
    backgroundColor: "var(--input-bg)",
    color: "var(--input-text)",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "monospace",
  },
  btnPrimary: {
    width: "100%",
    padding: "12px",
    backgroundColor: "var(--accent-color)",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnOutline: {
    flex: 1,
    padding: "10px 16px",
    backgroundColor: "transparent",
    color: "var(--accent-color)",
    border: "1.5px solid var(--accent-color)",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
  },
  secretBox: {
    backgroundColor: "var(--bg-secondary)",
    border: "1px solid var(--border-primary)",
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "12px",
    fontFamily: "monospace",
    letterSpacing: "2px",
    color: "var(--text-primary)",
    wordBreak: "break-all",
    textAlign: "center",
    width: "100%",
    boxSizing: "border-box",
  },
  backupGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
    width: "100%",
  },
  backupCode: {
    backgroundColor: "var(--bg-secondary)",
    border: "1px solid var(--border-primary)",
    borderRadius: "6px",
    padding: "8px 12px",
    fontSize: "12px",
    fontFamily: "monospace",
    letterSpacing: "1px",
    color: "var(--text-primary)",
    textAlign: "center",
  },
  error: {
    fontSize: "11px",
    color: "var(--error-color, #dc2626)",
    marginTop: "2px",
    alignSelf: "flex-start",
  },
  warningBox: {
    backgroundColor: "rgba(245,158,11,0.1)",
    border: "1px solid rgba(245,158,11,0.5)",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "11px",
    color: "#92400e",
    textAlign: "center",
    width: "100%",
    boxSizing: "border-box",
    lineHeight: 1.5,
  },
};

const MfaSetupModal = ({ onSuccess }) => {
  const [step, setStep]               = useState("loading"); // loading | qr | backup
  const [qrCode, setQrCode]           = useState("");
  const [secret, setSecret]           = useState("");
  const [verifyCode, setVerifyCode]   = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [error, setError]             = useState("");
  const [setupError, setSetupError]   = useState("");
  const [copied, setCopied]           = useState(false);

  const loadSetup = useCallback(async () => {
    try {
      const data = await apiRequest(buildApiUrl(API_ENDPOINTS.mfaSetup), {
        method: "POST",
      });
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setStep("qr");
    } catch (err) {
      setSetupError(err.message || "Error al iniciar la configuración de MFA.");
    }
  }, []);

  useEffect(() => {
    loadSetup();
  }, [loadSetup]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!verifyCode.trim()) {
      setError("Ingresa el código de verificación.");
      return;
    }
    setVerifyLoading(true);
    setError("");

    try {
      const data = await apiRequest(buildApiUrl(API_ENDPOINTS.mfaVerifySetup), {
        method: "POST",
        body: JSON.stringify({ code: verifyCode.trim() }),
      });
      setBackupCodes(data.backupCodes || []);
      setStep("backup");
    } catch (err) {
      setError(err.message || "Código incorrecto. Intenta de nuevo.");
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(backupCodes.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleDownload = () => {
    const content = [
      `Códigos de respaldo — ${APP_DISPLAY_NAME}`,
      "================================",
      "",
      ...backupCodes,
      "",
      "Cada código es de un solo uso.",
      "Guárdalos en un lugar seguro.",
    ].join("\n");
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "itsatapp-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Step: loading ──────────────────────────────────────────────────────────
  if (step === "loading") {
    return (
      <div style={s.overlay}>
        <div style={s.card}>
          {setupError ? (
            <p style={{ ...s.subtitle, color: "var(--error-color, #dc2626)" }}>
              {setupError}
            </p>
          ) : (
            <p style={s.subtitle}>Cargando configuración de MFA...</p>
          )}
        </div>
      </div>
    );
  }

  // ── Step: qr ──────────────────────────────────────────────────────────────
  if (step === "qr") {
    return (
      <div style={s.overlay}>
        <div style={s.card}>
          <h2 style={s.title}>Configura la verificación en dos pasos</h2>
          <p style={s.subtitle}>
            Escanea este código QR con tu app autenticadora (Google Authenticator,
            Authy, etc.) y luego ingresa el código de 6 dígitos para confirmar.
          </p>

          {qrCode && (
            <img
              src={qrCode}
              alt="Código QR para app autenticadora"
              style={{ width: "180px", height: "180px", borderRadius: "8px" }}
            />
          )}

          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
            <p style={{ ...s.subtitle, textAlign: "left" }}>
              ¿No puedes escanear el QR? Ingresa este código manualmente:
            </p>
            <div style={s.secretBox}>{secret}</div>
          </div>

          <form
            onSubmit={handleVerify}
            style={{ width: "100%", display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={s.label}>Código de verificación (6 dígitos)</label>
              <input
                type="text"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                placeholder="123456"
                maxLength={6}
                autoComplete="one-time-code"
                style={s.input}
              />
              {error && <span style={s.error}>{error}</span>}
            </div>

            <button type="submit" style={s.btnPrimary} disabled={verifyLoading}>
              {verifyLoading ? "Verificando..." : "Activar MFA"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Step: backup ──────────────────────────────────────────────────────────
  return (
    <div style={s.overlay}>
      <div style={s.card}>
        <h2 style={s.title}>MFA activado correctamente</h2>

        <div style={s.warningBox}>
          Guarda estos 10 códigos de respaldo en un lugar seguro. Son de un solo
          uso y <strong>no podrás recuperarlos</strong> después de cerrar esta ventana.
        </div>

        <div style={s.backupGrid}>
          {backupCodes.map((c) => (
            <div key={c} style={s.backupCode}>{c}</div>
          ))}
        </div>

        <div style={{ display: "flex", gap: "12px", width: "100%" }}>
          <button type="button" style={s.btnOutline} onClick={handleCopy}>
            {copied ? "¡Copiado!" : "Copiar códigos"}
          </button>
          <button type="button" style={s.btnOutline} onClick={handleDownload}>
            Descargar .txt
          </button>
        </div>

        <button type="button" style={s.btnPrimary} onClick={onSuccess}>
          Continuar a la aplicación
        </button>
      </div>
    </div>
  );
};

export default MfaSetupModal;
